/**
 * Error codes and curator-facing copy for the Smart photos API (bulk image
 * analysis v2).
 *
 * Mirrors `BulkImageAnalysisError` in nine-generics (16301-16399):
 *  - 16301-16349 are REQUEST-level: the API answers with them and the error's
 *    real HTTP status (NineBusinessRs envelope, code in `errorInformation[0]`,
 *    `additionalDetails` = { retryable, sessionId?, currentVersion?, problems? }).
 *  - 16350-16398 are STORED outcomes: written on a session / image / product /
 *    job (`BiaError`, `BiaItemError`) and shown from its `message`. The server's
 *    text is curator-safe by contract, so the table below is only the fallback
 *    when a row carries a code without a message.
 *  - 16399 (INTERNAL_ERROR) is used in both places.
 *
 * No imports, so a bare node script can load it.
 */

export const BIA_ERROR = {
  FEATURE_DISABLED: "16301",
  UNAUTHENTICATED: "16302",
  ROLE_CHECK_FAILED: "16303",
  NOT_ALLOWED: "16304",
  BUSINESS_NOT_CLAIMED: "16305",
  CLAIM_CHECK_FAILED: "16306",
  VALIDATION_ERROR: "16307",
  SESSION_NOT_FOUND: "16308",
  NOT_SESSION_OWNER: "16309",
  SESSION_ALREADY_ACTIVE: "16310",
  INVALID_SESSION_STATE: "16311",
  TOO_MANY_IMAGES: "16312",
  FILE_TOO_LARGE: "16313",
  UNSUPPORTED_IMAGE_TYPE: "16314",
  IMAGE_NOT_FOUND: "16315",
  UPLOAD_NOT_FOUND: "16316",
  NO_USABLE_IMAGES: "16317",
  PLAN_NOT_FOUND: "16318",
  PLAN_VERSION_CONFLICT: "16319",
  PLAN_STALE: "16320",
  PLAN_LOCKED: "16321",
  INVALID_PLAN: "16322",
  REVISION_LIMIT_REACHED: "16323",
  INSTRUCTION_TOO_LONG: "16324",
  JOB_ALREADY_RUNNING: "16325",
  NOTHING_TO_GENERATE: "16326",
  NOTHING_TO_CREATE: "16327",
  TOO_MANY_ITEMS: "16328",
  DAILY_LIMIT_EXCEEDED: "16329",
  PROVIDER_UNAVAILABLE: "16330",
  STORAGE_UNAVAILABLE: "16331",
  SECTION_NOT_FOUND: "16332",
  ITEM_NOT_IN_SESSION: "16333",
  ITEM_IN_PROGRESS: "16334",
  INVALID_MEDIA_REFERENCE: "16335",
  BUSINESS_MISMATCH: "16336",
  PRODUCT_VALIDATION_FAILED: "16337",
  // stored outcomes
  UPLOAD_EXPIRED: "16350",
  IMAGE_UNREADABLE: "16351",
  DUPLICATE_IMAGE: "16352",
  MODERATION_BLOCKED: "16353",
  NOTE_FAILED: "16354",
  NOTES_INCOMPLETE: "16355",
  PLAN_OUTPUT_INVALID: "16356",
  PLAN_FAILED: "16357",
  REVISION_FAILED: "16358",
  GENERATION_FAILED: "16359",
  GENERATION_OUTPUT_INVALID: "16360",
  VARIANTS_SPLIT: "16361",
  CREATE_FAILED: "16362",
  BATCH_EXPIRED: "16363",
  SESSION_EXPIRED: "16364",
  CANCELLED: "16365",
  INTERNAL_ERROR: "16399",
} as const;

/** Raised by the client itself (no server answer). */
export const BIA_CLIENT_ERROR = {
  NETWORK: "CLIENT_NETWORK",
  ABORTED: "CLIENT_ABORTED",
  UPLOAD_REJECTED: "CLIENT_UPLOAD_REJECTED",
  UPLOAD_EXPIRED: "CLIENT_UPLOAD_EXPIRED",
  IMAGE_UNSUPPORTED: "CLIENT_IMAGE_UNSUPPORTED",
  IMAGE_UNREADABLE: "CLIENT_IMAGE_UNREADABLE",
  IMAGE_TOO_LARGE: "CLIENT_IMAGE_TOO_LARGE",
} as const;

/** Messages copied from BulkImageAnalysisError (curator-safe by contract). */
export const BIA_ERROR_MESSAGES: Record<string, string> = {
  "16301": "Smart photo analysis is not available right now",
  "16302": "Please sign in again",
  "16303": "Could not verify your role. Please try again",
  "16304": "Only curators or admins can do this",
  "16305": "You can only analyse photos for businesses you have claimed",
  "16306": "Could not verify your claim on this business. Please try again",
  "16307": "Some fields are missing or invalid",
  "16308": "This photo analysis was not found",
  "16309": "Only the curator who started this analysis or an admin can do this",
  "16310": "Another photo analysis is in progress for this business",
  "16311": "This action is not allowed at this step",
  "16312": "This analysis already has the maximum number of photos",
  "16313": "This photo is larger than the allowed size",
  "16314": "Only JPEG, PNG or WebP photos can be used",
  "16315": "Photo not found in this analysis",
  "16316": "The photo upload has not finished yet",
  "16317": "Add at least one usable photo first",
  "16318": "No plan exists yet for this analysis",
  "16319": "The plan was changed in another tab. Reload to see the latest",
  "16320": "Photos changed since this plan. Re-run the plan first",
  "16321": "Products from this plan are already created, so it can't be changed",
  "16322": "This plan has problems that need fixing first",
  "16323": "This analysis has reached its revision limit",
  "16324": "Instructions are too long",
  "16325": "Something is already running for this analysis. Wait for it to finish",
  "16326": "There are no products waiting to be written",
  "16327": "There are no products ready to create",
  "16328": "Too many products in one request",
  "16329": "Daily limit reached. Please try again tomorrow",
  "16330": "The AI service is unavailable right now. Please try again shortly",
  "16331": "File storage is unavailable right now. Please try again shortly",
  "16332": "That section no longer exists for this business",
  "16333": "This product does not belong to this analysis",
  "16334": "This product is being created right now",
  "16335": "A photo on this product does not belong to this analysis",
  "16336": "This product belongs to a different business",
  "16337": "This product is missing required details",
  "16350": "This photo was never uploaded, so it was removed",
  "16351": "This photo could not be opened. It may be damaged",
  "16352": "This photo is an exact copy of another one",
  "16353": "This photo was blocked by content safety checks",
  "16354": "The AI could not describe this photo",
  "16355": "Too many photos could not be described. Try again",
  "16356": "The AI's plan was unusable. Try again",
  "16357": "The plan could not be made. Try again",
  "16358": "The change could not be applied. The previous plan is still in place",
  "16359": "The AI could not write this product",
  "16360": "The AI's answer for this product was unusable",
  "16361": "Variants were kept as separate products because this listing type has no variants",
  "16362": "This product could not be created",
  "16363": "Processing took too long and was stopped. Retry the failed products",
  "16364": "This analysis expired after a long time without activity",
  "16365": "This analysis was cancelled",
  "16399": "Something went wrong. Please try again",
  CLIENT_NETWORK: "Couldn't reach the server. Check your connection and try again.",
  CLIENT_ABORTED: "Cancelled.",
  CLIENT_UPLOAD_REJECTED: "The photo was rejected by storage. Please try again.",
  CLIENT_UPLOAD_EXPIRED: "The upload link expired. Please try again.",
  CLIENT_IMAGE_UNSUPPORTED: "This browser can't open this kind of photo. Convert it to JPEG and add it again.",
  CLIENT_IMAGE_UNREADABLE: "This photo could not be opened. It may be damaged.",
  CLIENT_IMAGE_TOO_LARGE: "This photo is still too large after shrinking it.",
};

/** Codes where trying the SAME request again can succeed (BulkImageAnalysisError#isRetryable). */
const RETRYABLE_CODES: ReadonlySet<string> = new Set([
  "16303",
  "16306",
  "16316",
  "16319",
  "16325",
  "16329",
  "16330",
  "16331",
  "16334",
  "16354",
  "16355",
  "16356",
  "16357",
  "16358",
  "16359",
  "16360",
  "16362",
  "16363",
  "16399",
  "CLIENT_NETWORK",
  "CLIENT_UPLOAD_REJECTED",
  "CLIENT_UPLOAD_EXPIRED",
]);

export const isRetryableBiaCode = (code: string): boolean => RETRYABLE_CODES.has(code);

/** 16301-16399 — anything this feature's backend produced. */
export const isBiaCode = (code: string): boolean => /^163\d\d$/.test(code);

/**
 * Curator-friendly text for an error code. Unknown codes (a newer server, or an
 * HTTP_5xx the gateway produced) use `fallback`.
 */
export function biaErrorMessage(code: string | null | undefined, fallback: string): string {
  if (!code) return fallback;
  return BIA_ERROR_MESSAGES[code] ?? fallback;
}

/** Fallback when the server (or a gateway) gives no usable message at all. */
export const BIA_GENERIC_ERROR = "Something went wrong. Please try again.";

/** GET retry waits: 600 ms then 1.8 s (two retries), jittered +-20%. */
export function biaRetryDelay(attempt: number, random: () => number = Math.random): number {
  const base = 600 * Math.pow(3, Math.max(0, attempt));
  return Math.round(base * (0.8 + random() * 0.4));
}

/** Upload retry waits (per file): 1 s, 2 s, 4 s, jittered +-25%. */
export function biaUploadRetryDelay(attempt: number, random: () => number = Math.random): number {
  const base = 1000 * Math.pow(2, Math.max(0, attempt));
  return Math.round(base * (0.75 + random() * 0.5));
}
